import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { NAVIGATION_SCROLL_RESTORED, useNavigationScrollRestoration } from '../../navigation/NavigationScrollProvider';

/** Observe the existing content scroller. Never write its position or dimensions. */
export function useCrmAutoHideHeader(enabled: boolean, target: HTMLElement | null, header: RefObject<HTMLElement | null>, locked: boolean) {
  const [hidden, setHidden] = useState(false);
  const location = useLocation();
  const isRestoring = useNavigationScrollRestoration();
  useEffect(() => {
    setHidden(false);
    if (!enabled || !target || locked) return;
    let last = target.scrollTop;
    let movement = 0;
    let intentUntil = 0;
    let intentDirection = 0;
    const eventTarget = target === document.scrollingElement ? document : target;
    const reset = () => { last = target.scrollTop; movement = 0; intentUntil = 0; setHidden(false); };
    const intent = (event?: Event) => {
      intentUntil = performance.now() + 500;
      intentDirection = event instanceof WheelEvent ? Math.sign(event.deltaY) : 0;
      if (intentDirection < 0) { movement = 0; setHidden(false); }
    };
    const keyboard = (event: KeyboardEvent) => {
      if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)
        && !(event.target instanceof Element && event.target.closest('input,textarea,select,[contenteditable=true]'))) intent();
    };
    const drag = (event: PointerEvent) => { if (event.buttons === 1 && event.target === target) intent(); };
    const scroll = () => {
      const y = Math.max(0, target.scrollTop);
      const delta = y - last;
      last = y;
      if (isRestoring(target) || performance.now() > intentUntil || header.current?.contains(document.activeElement)) {
        movement = 0;
        return;
      }
      intentUntil = performance.now() + 180; // Include the current touch/wheel momentum.
      if (intentDirection && delta && Math.sign(delta) !== intentDirection) { movement = 0; return; }
      movement = Math.sign(delta) === Math.sign(movement) ? movement + delta : delta;
      if (y < 32) { setHidden(false); movement = 0; }
      else if (Math.abs(movement) >= 16) { setHidden(movement > 0); movement = 0; }
    };
    const focus = () => setHidden(false);
    const headerNode = header.current;
    eventTarget.addEventListener('scroll', scroll, { passive: true });
    eventTarget.addEventListener('wheel', intent, { passive: true });
    eventTarget.addEventListener('touchmove', intent, { passive: true });
    target.addEventListener('pointermove', drag, { passive: true });
    target.addEventListener(NAVIGATION_SCROLL_RESTORED, reset);
    document.addEventListener('keydown', keyboard);
    headerNode?.addEventListener('focusin', focus);
    return () => {
      eventTarget.removeEventListener('scroll', scroll);
      eventTarget.removeEventListener('wheel', intent);
      eventTarget.removeEventListener('touchmove', intent);
      target.removeEventListener('pointermove', drag);
      target.removeEventListener(NAVIGATION_SCROLL_RESTORED, reset);
      document.removeEventListener('keydown', keyboard);
      headerNode?.removeEventListener('focusin', focus);
    };
  }, [enabled, target, locked, location.key, header, isRestoring]);
  return enabled && !locked && hidden;
}
